import { useMemo, useState } from "react";
import { Loader2, Package, RotateCcw, Ticket, Truck } from "lucide-react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type PeriodoRapido = "todos" | "hoje" | "7dias" | "30dias" | "mes";

function dataParaInput(data: Date) {
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  const dia = String(data.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
}

function dataHora(valor: unknown) {
  if (!valor) return null;
  const texto = String(valor);
  return new Date(texto.includes("T") ? texto : texto.replace(" ", "T")).getTime();
}

function formatarData(valor: unknown) {
  const timestamp = dataHora(valor);
  return timestamp === null ? "—" : new Date(timestamp).toLocaleDateString("pt-BR");
}

function calcularPrazoPedido(pedido: any, agora: number) {
  if (!pedido.dataPedido || !pedido.prazoPreparo) return null;
  const [horas, minutos, segundos] = String(pedido.prazoPreparo).split(":").map(Number);
  if ([horas, minutos, segundos].some(Number.isNaN)) return null;
  const adicionarTempoUtil = (inicio: number, duracao: number) => {
    const inicioExpediente = 8;
    const fimExpediente = 17;
    let cursor = new Date(inicio);
    let restante = duracao;
    while (restante > 0) {
      const inicioDia = new Date(cursor);
      inicioDia.setHours(inicioExpediente, 0, 0, 0);
      const fimDia = new Date(cursor);
      fimDia.setHours(fimExpediente, 0, 0, 0);
      if (cursor.getDay() === 0 || cursor.getDay() === 6 || cursor.getTime() >= fimDia.getTime()) {
        cursor.setDate(cursor.getDate() + 1);
        cursor.setHours(inicioExpediente, 0, 0, 0);
        continue;
      }
      if (cursor.getTime() < inicioDia.getTime()) cursor = inicioDia;
      const trecho = Math.min(restante, fimDia.getTime() - cursor.getTime());
      cursor = new Date(cursor.getTime() + trecho);
      restante -= trecho;
    }
    return cursor.getTime();
  };
  const diferencaTempoUtil = (inicio: number, fim: number) => {
    const inicioExpediente = 8;
    const fimExpediente = 17;
    const sinal = fim >= inicio ? 1 : -1;
    let cursor = new Date(Math.min(inicio, fim));
    const limite = Math.max(inicio, fim);
    let total = 0;
    while (cursor.getTime() < limite) {
      const inicioDia = new Date(cursor);
      inicioDia.setHours(inicioExpediente, 0, 0, 0);
      const fimDia = new Date(cursor);
      fimDia.setHours(fimExpediente, 0, 0, 0);
      const proximoDia = new Date(cursor);
      proximoDia.setHours(24, 0, 0, 0);
      const fimTrecho = Math.min(proximoDia.getTime(), limite);
      if (cursor.getDay() !== 0 && cursor.getDay() !== 6) {
        const inicioTrecho = Math.max(cursor.getTime(), inicioDia.getTime());
        const fimUtil = Math.min(fimTrecho, fimDia.getTime());
        if (fimUtil > inicioTrecho) total += fimUtil - inicioTrecho;
      }
      cursor = new Date(fimTrecho);
    }
    return total * sinal;
  };
  const limite = adicionarTempoUtil(dataHora(pedido.dataPedido)!, (horas * 3600 + minutos * 60 + segundos) * 1000);
  const finalizadoEm = pedido.dataAutorizacaoNfe ? dataHora(pedido.dataAutorizacaoNfe) : null;
  const diferenca = diferencaTempoUtil(finalizadoEm ?? agora, limite);
  const totalMinutos = Math.floor(Math.abs(diferenca) / 60000);
  const restante = `${diferenca < 0 ? "-" : ""}${String(Math.floor(totalMinutos / 60)).padStart(2, "0")}:${String(totalMinutos % 60).padStart(2, "0")}`;
  return {
    restante,
    status: finalizadoEm
      ? (diferenca >= 0 ? "Finalizado no prazo" : "Finalizado em atraso")
      : (diferenca >= 0 ? "No prazo" : "Em atraso"),
  };
}

function MetricCard({ title, value, description, href, children }: { title: string; value: string | number; description?: string; href?: string; children?: React.ReactNode }) {
  const content = (
    <Card className={href ? "min-w-0 transition-colors hover:border-primary" : "min-w-0"}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{value}</div>
        {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
        {children}
      </CardContent>
    </Card>
  );
  return href ? <a href={href} className="block min-w-0">{content}</a> : content;
}

export default function DashboardOperacao() {
  const { user } = useGoogleAuth();
  const isAdmin = user?.role === "admin";
  const [emitenteId, setEmitenteId] = useState("__all__");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [periodoAtivo, setPeriodoAtivo] = useState<PeriodoRapido>("todos");
  const emitenteIdNumero = emitenteId === "__all__" ? undefined : Number(emitenteId);
  const topItensArmazenagemInput = useMemo(
    () => emitenteIdNumero ? { emitenteId: emitenteIdNumero } : undefined,
    [emitenteIdNumero]
  );

  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();
  const { data: resumoResolve, isLoading: carregandoResolve } = trpc.resolve.dashboard.operacao.useQuery({
    emitenteId: emitenteIdNumero,
    dataInicio: dataInicio || undefined,
    dataFim: dataFim || undefined,
  });
  const { data: pedidosAbertos = [], isLoading: carregandoB2b } = trpc.pedidos.listOpen.useQuery();
  const { data: resumoMecom, isLoading: carregandoMecom } = trpc.mecom.dashboard.useQuery({
    emitenteIds: emitenteIdNumero ? [emitenteIdNumero] : undefined,
    dataInicio: dataInicio || undefined,
    dataFim: dataFim || undefined,
  });
  const { data: topItensArmazenagem = [], isLoading: carregandoArmazenagem } = trpc.dashboardOperacao.topItensArmazenagem.useQuery(topItensArmazenagemInput);

  const agora = Date.now();
  const inicioTimestamp = dataInicio ? new Date(`${dataInicio}T00:00:00`).getTime() : null;
  const fimTimestamp = dataFim ? new Date(`${dataFim}T23:59:59.999`).getTime() : null;
  const pedidosB2b = useMemo(() => (pedidosAbertos as any[]).filter((pedido) => {
    const dataPedido = dataHora(pedido.dataPedido);
    return (emitenteIdNumero === undefined || Number(pedido.emitenteId) === emitenteIdNumero)
      && (inicioTimestamp === null || (dataPedido !== null && dataPedido >= inicioTimestamp))
      && (fimTimestamp === null || (dataPedido !== null && dataPedido <= fimTimestamp));
  }), [pedidosAbertos, emitenteIdNumero, inicioTimestamp, fimTimestamp]);
  const pedidosEmAtraso = pedidosB2b.filter((pedido) => calcularPrazoPedido(pedido, agora)?.status === "Em atraso").length;
  const pedidosNoPrazo = pedidosB2b.filter((pedido) => calcularPrazoPedido(pedido, agora)?.status === "No prazo").length;
  const bipsMecom = (resumoMecom?.recentBips as any[] | undefined) ?? [];
  const devolucoesMecom = bipsMecom.filter((pedido) => pedido.status === "devolvido").length;
  const transportadoras = useMemo(() => Array.from(bipsMecom.reduce((acumulado, pedido) => {
    const nome = pedido.transportadoraNome || "Sem transportadora";
    acumulado.set(nome, (acumulado.get(nome) || 0) + 1);
    return acumulado;
  }, new Map<string, number>()).entries()).sort(([, totalA], [, totalB]) => totalB - totalA || 0), [bipsMecom]);

  const totalSolicitacoes = Number(resumoResolve?.periodo?.totalSolicitacoes ?? 0);
  const operacaoGarantida = Number(resumoResolve?.periodo?.operacaoGarantida ?? 0);
  const totalMecom = bipsMecom.length;
  const baseOperacao = pedidosB2b.length + totalMecom;
  const percentualGarantida = baseOperacao > 0 ? (operacaoGarantida / baseOperacao) * 100 : 0;

  const paramsResolve = new URLSearchParams();
  if (isAdmin && emitenteIdNumero) paramsResolve.set("emitenteId", String(emitenteIdNumero));
  const paramsPedidos = new URLSearchParams();
  if (dataInicio) paramsPedidos.set("dataInicio", dataInicio);
  if (dataFim) paramsPedidos.set("dataFim", dataFim);
  if (isAdmin && emitenteIdNumero) paramsPedidos.set("emitenteId", String(emitenteIdNumero));
  const linkResolve = (extra: Record<string, string>) => {
    const parametros = new URLSearchParams(paramsResolve);
    Object.entries(extra).forEach(([chave, valor]) => parametros.set(chave, valor));
    return `/resolve?${parametros.toString()}`;
  };
  const linkPedidos = (prazo: "atraso" | "no-prazo") => {
    const parametros = new URLSearchParams(paramsPedidos);
    parametros.set("prazo", prazo);
    return `/open-orders?${parametros.toString()}`;
  };

  const aplicarPeriodo = (periodo: PeriodoRapido) => {
    const hoje = new Date();
    const fim = dataParaInput(hoje);
    if (periodo === "todos") {
      setDataInicio("");
      setDataFim("");
    } else if (periodo === "hoje") {
      setDataInicio(fim);
      setDataFim(fim);
    } else if (periodo === "7dias") {
      const inicio = new Date(hoje);
      inicio.setDate(inicio.getDate() - 6);
      setDataInicio(dataParaInput(inicio));
      setDataFim(fim);
    } else if (periodo === "30dias") {
      const inicio = new Date(hoje);
      inicio.setDate(inicio.getDate() - 29);
      setDataInicio(dataParaInput(inicio));
      setDataFim(fim);
    } else {
      const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      setDataInicio(dataParaInput(inicio));
      setDataFim(fim);
    }
    setPeriodoAtivo(periodo);
  };

  const pendentesPorTipo = (resumoResolve?.pendentes?.porTipo as any[] | undefined) ?? [];
  const pendentesFullPorTipo = (resumoResolve?.pendentesFull?.porTipo as any[] | undefined) ?? [];
  const pendenteMaisAntiga = resumoResolve?.pendentes?.maisAntiga as any;
  const carregando = carregandoResolve || carregandoB2b || carregandoMecom || carregandoArmazenagem;

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-6">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="text-2xl font-bold">Dashboard da Operação</h1>
          <p className="text-sm text-muted-foreground">Indicadores operacionais consolidados.</p>
        </div>
        {carregando && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Atualizando indicadores</div>}
      </div>

      <Card>
        <CardContent className="flex flex-col gap-4 pt-6">
          <div className="flex flex-wrap gap-2">
            {([
              ["todos", "Todo período"],
              ["hoje", "Hoje"],
              ["7dias", "Últimos 7 dias"],
              ["30dias", "Últimos 30 dias"],
              ["mes", "Mês atual"],
            ] as Array<[PeriodoRapido, string]>).map(([periodo, rotulo]) => (
              <Button key={periodo} size="sm" variant={periodoAtivo === periodo ? "default" : "outline"} onClick={() => aplicarPeriodo(periodo)}>{rotulo}</Button>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            <div className="min-w-0"><label className="mb-1 block text-xs font-medium text-muted-foreground">Data inicial</label><Input className="w-full" type="date" value={dataInicio} onChange={(evento) => { setDataInicio(evento.target.value); setPeriodoAtivo("todos"); }} /></div>
            <div className="min-w-0"><label className="mb-1 block text-xs font-medium text-muted-foreground">Data final</label><Input className="w-full" type="date" value={dataFim} onChange={(evento) => { setDataFim(evento.target.value); setPeriodoAtivo("todos"); }} /></div>
            <div className="min-w-0"><label className="mb-1 block text-xs font-medium text-muted-foreground">Emitente</label><Select value={emitenteId} onValueChange={setEmitenteId}><SelectTrigger className="w-full min-w-0"><SelectValue placeholder="Todos os emitentes permitidos" /></SelectTrigger><SelectContent><SelectItem value="__all__">Todos os emitentes permitidos</SelectItem>{(emitentes as any[]).map((emitente) => <SelectItem key={emitente.id} value={String(emitente.id)}>{emitente.nomeFantasia || emitente.razaoSocial}</SelectItem>)}</SelectContent></Select></div>
          </div>
        </CardContent>
      </Card>

      <section className="space-y-3">
        <div><h2 className="text-lg font-semibold">Pendentes</h2><p className="text-sm text-muted-foreground">Não respeitam o período selecionado.</p></div>
        <div className="grid gap-4 lg:grid-cols-2">
          <MetricCard title="Pendentes no Resolve" value={Number(resumoResolve?.pendentes?.total ?? 0)} href={linkResolve({ status: "Pendente" })} description={pendenteMaisAntiga ? `Mais antiga: #${pendenteMaisAntiga.ticket} em ${formatarData(pendenteMaisAntiga.criadoEm)}` : "Nenhuma solicitação pendente"}>
            <div className="mt-4 space-y-1 text-xs text-muted-foreground">{pendentesPorTipo.map((tipo) => <div key={tipo.tipo} className="flex justify-between"><span>{tipo.tipo}</span><strong className="text-foreground">{tipo.total}</strong></div>)}</div>
          </MetricCard>
          <MetricCard title="Pendente Full" value={Number(resumoResolve?.pendentesFull?.total ?? 0)} href={linkResolve({ status: "Pendente e Em progresso", temaContains: "full" })} description="Tema contém “full”">
            <div className="mt-4 space-y-1 text-xs text-muted-foreground">{pendentesFullPorTipo.map((tipo) => <div key={tipo.tipo} className="flex justify-between"><span>{tipo.tipo}</span><strong className="text-foreground">{tipo.total}</strong></div>)}</div>
          </MetricCard>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Operação no período</h2>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <MetricCard title="Total de solicitações" value={totalSolicitacoes} description="Resolve · criado em" />
          <MetricCard title="Operação garantida" value={operacaoGarantida} description="Resolve · status Operação Garantida" />
          <MetricCard title="Pedidos B2B" value={pedidosB2b.length} description="Pedidos em Aberto · criado em" />
          <MetricCard title="Pedidos MECOM" value={totalMecom} description="MECOM · entrada" />
          <MetricCard title="% operação garantida" value={`${percentualGarantida.toFixed(1)}%`} description="Sobre B2B + MECOM" />
        </div>
      </section>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Package className="h-5 w-5" />Top 10 itens com mais armazenamento (m³)</CardTitle></CardHeader>
        <CardContent>{topItensArmazenagem.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum item de armazenagem disponível.</p> : <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b bg-muted/40"><th className="px-3 py-2 text-left">SKU</th><th className="px-3 py-2 text-left">Descrição</th><th className="px-3 py-2 text-right">m³</th></tr></thead><tbody>{topItensArmazenagem.map((item: any, indice: number) => <tr key={`${item.sku}-${indice}`} className="border-b"><td className="px-3 py-2 font-mono">{item.sku}</td><td className="px-3 py-2">{item.descricao || "-"}</td><td className="px-3 py-2 text-right tabular-nums">{Number(item.metragemTotalM3).toFixed(6)}</td></tr>)}</tbody></table></div>}</CardContent>
      </Card>

      <section className="grid min-w-0 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <MetricCard title="Pedidos em atraso B2B" value={pedidosEmAtraso} href={linkPedidos("atraso")} description="Em atraso" />
        <MetricCard title="Pedidos no prazo B2B" value={pedidosNoPrazo} href={linkPedidos("no-prazo")} description="No prazo" />
        <MetricCard title="Devoluções MECOM" value={devolucoesMecom} description="MECOM · entrada" />
      </section>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Truck className="h-5 w-5" />Pedidos por transportadora MECOM</CardTitle></CardHeader>
        <CardContent>{transportadoras.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum pedido MECOM no período.</p> : <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">{transportadoras.map(([transportadora, total]) => <div key={transportadora} className="flex items-center justify-between rounded-md border px-3 py-2"><span className="truncate pr-3 text-sm">{transportadora}</span><strong>{total}</strong></div>)}</div>}</CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-3 text-muted-foreground">
        <div className="flex items-center gap-2 text-sm"><Ticket className="h-4 w-4" />Resolve por criação</div>
        <div className="flex items-center gap-2 text-sm"><Package className="h-4 w-4" />B2B por criação</div>
        <div className="flex items-center gap-2 text-sm"><RotateCcw className="h-4 w-4" />MECOM por entrada</div>
      </div>
    </div>
  );
}
